/** Sidecar describing a template mesh and its text zones. See PLAN.md. */
export type Vec3 = [number, number, number]
export type ZoneMode = 'emboss' | 'engrave'
/** 'richtext' (#53): several lines that wrap inside the zone and can be aligned; edited in a dialog. */
export type ZoneKind = 'text' | 'symbol' | 'richtext'
export type ZoneAlign = 'left' | 'center' | 'right'

export interface Zone {
  id: string
  label: string
  /** What goes in the zone. Default 'text'. Symbol zones are optional adornments. */
  kind?: ZoneKind
  /** Local frame on the surface: text plane is centred at origin, +Z = normal. */
  origin: Vec3
  normal: Vec3
  up: Vec3
  /** Bounding box (mm) the text must fit inside. */
  width: number
  height: number
  mode: ZoneMode
  /** Emboss height / engrave depth in mm. */
  depth: number
  /** Richtext zones: most lines the wrapped text may take before it shrinks to fit. */
  maxLines: number
  default: string
  /** Richtext zones: the starting alignment (default 'center'). */
  align?: ZoneAlign
  font?: string
  /** Which template part the zone is on. Default: the first part. */
  part?: string
  /**
   * Emboss-only: also add a thin layer of the shape dilated by `offset` mm,
   * `height` mm tall, so detached bits (the dot on an i, tight script
   * joins) are bridged. Used where the text *is* the model (cake topper).
   */
  backing?: { offset: number; height: number }
  /** Default palette index for the zone's colour; omitted = same as its part. */
  colour?: number
  /**
   * Curved text: lay the (single) line along a circular arc centred on
   * `origin`, in the zone plane. `radius` is the inner edge of the text
   * band (mm) — the same value on a top and a bottom arc puts both in the
   * same ring — and `sweep` the maximum angle (degrees) it may occupy. `top` reads
   * clockwise over the top with letter tops pointing outward; `bottom`
   * reads anticlockwise under the bottom with tops pointing inward — the
   * usual coin / casino-chip arrangement. `height` limits glyph height;
   * `width` is ignored (the arc length is the width).
   */
  arc?: { radius: number; sweep: number; side: 'top' | 'bottom' }
  /**
   * Extra placements of the same content, as offsets in the zone's own
   * plane ([x, y, 0] from `origin`) — one user choice, many placements: an
   * adornment patterned around a border, or repeated around a mug. With
   * `wrap`, the offsets run along the unrolled surface.
   */
  repeat?: Vec3[]
  /**
   * Wrap the content around a cylinder of this radius, whose axis is
   * parallel to `up` and sits `radius` below `origin` along `normal` — so
   * `origin` is a point on the cylinder and the zone's local X is arc
   * length around it. Unlike `arc` (which bends a line within a flat
   * face), this bends the surface itself: text around a mug.
   */
  wrap?: { radius: number }
  /**
   * Zones sharing the same `vcenter` key are one vertical stack (e.g. a
   * name line above an epitaph). If exactly one of them ends up with
   * content, it's re-centred on the group's midpoint origin instead of
   * sitting at its own fixed spot — so a single line doesn't look stranded
   * next to a gap where the others would have been. With zero or several
   * filled, every zone keeps its own configured origin.
   */
  vcenter?: string
  /**
   * Use `origin` instead of the configured one when the named zone (e.g. an
   * adornment beside this text) has no content — so text offset to leave
   * room for an icon re-centres when that icon isn't used. Combines with
   * `vcenter`: the swapped-in origin is what feeds the group's midpoint.
   */
  centerIfEmpty?: { zone: string; origin: Vec3 }
}

/** A separately printable solid of the template, colourable on its own. */
export interface TemplatePart {
  id: string
  label: string
  /** Mesh file, relative to the template folder. GLB. */
  mesh: string
  /** Default index into the palette. */
  colour?: number
}

export interface Template {
  id: string
  name: string
  /** Defaults to true; false hides the template from the catalogue. */
  published?: boolean
  /** True once a real print of this template has been checked. Shows a badge. */
  verified?: boolean
  /** A short description of the model, shown in the editor. */
  notes?: string
  /**
   * Markdown print instructions (orientation, supports, assembly), apart from `notes`: shown by the
   * editor's 'Print instructions' link and included in the STL zip's README. Templates without it fall
   * back to using `notes` there, as before.
   */
  printInstructions?: string
  /** Admin-chosen home page highlight (#56); only ever set by the server from an override, never from the file. */
  highlighted?: boolean
  /** Admin-deleted (hidden from everyone but admins); only ever set by the server from an override. */
  deleted?: boolean
  /**
   * Parts must be printed together exactly as placed (print-in-place
   * hinges etc.). The STL download is then one merged file rather than a
   * zip of separate parts, so they can't be arranged apart by mistake.
   */
  printInPlace?: boolean
  /** Single-part shorthand: mesh file relative to the template folder. */
  mesh?: string
  /** Multi-part templates: each part is its own solid (they must not overlap). */
  parts?: TemplatePart[]
  /** Suggested palette (1–6 hex colours). */
  colours?: string[]
  units: 'mm'
  tags: string[]
  thumb?: string
  zones: Zone[]
  /** Who designed this template. */
  author?: string
}

/** Normalise `mesh` / `parts` into a parts list. */
export function templateParts(t: Template): TemplatePart[] {
  if (t.parts?.length) return t.parts
  if (t.mesh) return [{ id: 'body', label: 'Body', mesh: t.mesh }]
  throw new Error(`template ${t.id}: no mesh or parts`)
}

export interface CatalogueEntry {
  id: string
  name: string
  tags: string[]
  verified?: boolean
  author?: string
  /** Visible to everyone. Only ever true for verified templates. */
  published?: boolean
  /** template.json asks to be published but it isn't live yet (unverified, or an admin said no). */
  publishRequested?: boolean
  /** Admin-chosen (#56): shown in the home page highlights strip. Never comes from template.json. */
  highlighted?: boolean
  /** Thumbs up count (#40). Down votes are never shown to the public. */
  upVotes?: number
  /** Only sent to admins and the model's author. */
  downVotes?: number
  /** The signed-in user's own vote: 1, -1 or 0. */
  myVote?: number
  /** ISO date the template was added (template.json `created`, else its file date). */
  created?: string
  /** Photo/video URLs in upload order (#79): sent with the catalogue so a card needs no request of its own. */
  photos?: string[]
  /** For each entry of `photos`, its small version (a few hundred px) or null: what the gallery shows instead of the multi-MB original. */
  photosSmall?: (string | null)[]
  /** A pre-made thumbnail image (#79), if one has been generated for the template's current model. */
  thumb?: string
  /** Which version of the model a new thumbnail must be rendered for; sent with the upload. */
  thumbVer?: string
}

/** Per-zone user settings. */
export interface ZoneSettings {
  /** Text zones: the text. */
  text: string
  /** Symbol zones: library id, or '' for none. */
  symbol: string
  mode: ZoneMode
  depth: number
  font: string
  /** Richtext zones: line alignment within the zone. */
  align: ZoneAlign
  /** 0..1 multiplier on the auto-fit size. */
  scale: number
  /** Index into Design.colours, or -1 = same as the part it sits on (plain union/cut). */
  colour: number
}

export type DesignSettings = Record<string, ZoneSettings>

/** Everything a share link carries: per-zone settings, part colours, palette. */
export interface Design {
  zones: DesignSettings
  /** Colour index per template part id. */
  parts: Record<string, number>
  /** 1–6 hex colours. Zones default to their part's colour. */
  colours: string[]
}

export const MAX_COLOURS = 6
export const DEFAULT_COLOURS = ['#e6d9bd', '#c0392b', '#2c3e50', '#d4a017', '#f4f4f0', '#27ae60']

export function defaultDesign(t: Template, defaultFont: string): Design {
  const colours = (t.colours?.length ? t.colours : [DEFAULT_COLOURS[0]]).slice(0, MAX_COLOURS)
  const zones: DesignSettings = {}
  for (const z of t.zones) {
    const sym = z.kind === 'symbol'
    const colour = z.colour !== undefined && z.colour >= 0 && z.colour < colours.length ? z.colour : -1
    zones[z.id] = { text: sym ? '' : z.default, symbol: sym ? z.default : '', mode: z.mode, depth: z.depth, font: z.font ?? defaultFont, align: z.align ?? 'center', scale: 1, colour }
  }
  const parts: Record<string, number> = {}
  const tp = templateParts(t)
  for (const p of tp) parts[p.id] = p.colour !== undefined && p.colour < colours.length ? p.colour : 0
  return { zones, parts, colours }
}

/**
 * Merge partial, untrusted per-zone overrides onto a template's defaults —
 * shared by share.ts's decodeDesign (from a base64url URL payload) and the
 * server-side batch-render API (from a JSON request body). Bad or missing
 * fields silently fall back to the zone's default rather than erroring, so
 * a caller never has to get every field right to get something back.
 */
export function mergeZoneSettings(
  t: Template,
  defaults: DesignSettings,
  raw: Record<string, Partial<ZoneSettings>>,
  colourCount: number,
  isValidFont: (name: string) => boolean,
): DesignSettings {
  const zones: DesignSettings = {}
  for (const z of t.zones) {
    const d = defaults[z.id], v = raw[z.id] ?? {}
    zones[z.id] = {
      text: typeof v.text === 'string' ? v.text : d.text,
      symbol: typeof v.symbol === 'string' && /^[\w-]*$/.test(v.symbol) ? v.symbol : d.symbol,
      mode: v.mode === 'emboss' || v.mode === 'engrave' ? v.mode : d.mode,
      depth: typeof v.depth === 'number' && v.depth > 0 && v.depth <= 10 ? v.depth : d.depth,
      font: typeof v.font === 'string' && isValidFont(v.font) ? v.font : d.font,
      align: v.align === 'left' || v.align === 'center' || v.align === 'right' ? v.align : d.align,
      scale: typeof v.scale === 'number' && v.scale > 0 && v.scale <= 1 ? v.scale : d.scale,
      colour: typeof v.colour === 'number' && Number.isInteger(v.colour) && v.colour >= -1 && v.colour < colourCount ? v.colour : d.colour,
    }
  }
  return zones
}

export const templateBase = (id: string) => `/templates/${id}/`

/** The whole visible catalogue in one go (no paging): for the stats page and external-style uses. */
export async function loadCatalogue(): Promise<CatalogueEntry[]> {
  const r = await fetch('/api/v1/templates')
  if (!r.ok) throw new Error(`catalogue: ${r.status}`)
  return r.json()
}

export type GallerySort = 'name' | 'recent' | 'popular'

/** Gallery filters (#39); applied server-side so paging stays correct. Empty/false means "no filter". */
export interface CatalogueQuery {
  q?: string
  author?: string
  mine?: boolean
  verified?: string
  published?: string
  highlighted?: boolean
  sort?: GallerySort
}

/** One page of the catalogue plus the total number of matches (X-Total-Count). */
export async function loadCataloguePage(query: CatalogueQuery, offset: number, limit: number): Promise<{ items: CatalogueEntry[]; total: number }> {
  const p = new URLSearchParams({ offset: String(offset), limit: String(limit) })
  for (const [k, v] of Object.entries(query)) if (v) p.set(k, String(v))
  const r = await fetch(`/api/v1/templates?${p}`)
  if (!r.ok) throw new Error(`catalogue: ${r.status}`)
  const items: CatalogueEntry[] = await r.json()
  return { items, total: Number(r.headers.get('X-Total-Count') ?? items.length) }
}

/** Authors of the templates this viewer can see, for the author filter. */
export async function loadAuthors(): Promise<string[]> {
  try {
    const r = await fetch('/api/v1/templates/authors')
    return r.ok ? await r.json() : []
  } catch {
    return []
  }
}

export async function loadTemplate(id: string): Promise<Template> {
  const r = await fetch(`/api/v1/templates/${id}`)
  if (!r.ok) throw new Error(`template ${id}: ${r.status}`)
  return r.json()
}
